/**
 * Held-lock registry (power#1224)
 *
 * A tab-scoped record of which edit locks THIS tab currently owns, so that a
 * deliberate reload can release them *before* the document goes away.
 *
 * Why this exists: lock ownership was previously only knowable from whichever
 * page happened to be mounted — the workflow lock lives in
 * `CollaborationContext`, the timeline lock inside `useTimelineLock`, and
 * `Workflow.tsx` tracks it again in a component-local ref. Nothing could answer
 * "what does this tab hold?", which is exactly what the new-version reload needs.
 *
 * Scope, deliberately narrow:
 *   - This registry is a MIRROR, never a source of truth. The lock hooks stay
 *     authoritative; they just report into it.
 *   - It does NOT replace the existing `pagehide`/`beforeunload` keepalive
 *     handlers (`src/utils/releaseLockOnUnload.ts`). Those remain the safety net
 *     for tab close and crash. This serves the deliberate-reload path only.
 *
 * `lockId` is carried even though the DELETE endpoints are resource-id-only
 * today: power#1142 makes release lockId-scoped, and retrofitting the field
 * later would mean revisiting every registration site.
 *
 * Each entry carries the owning hook's OWN `release` callback rather than a URL.
 * That is deliberate: `useWorkflowLocking.releaseWorkflowLock` and
 * `useTimelineLock.releaseLock` already clear the `sessionStorage` lockId key
 * before the DELETE, are idempotent, and swallow their own errors. Re-issuing a
 * raw DELETE from here would bypass all of that and leave the tab-scoped lockId
 * key pointing at a released lock — which the reloaded page would then try to
 * REFRESH (see `src/utils/lockSessionStorage.ts`).
 *
 * `release` MUST be a stable identity backed by a ref (the house pattern — cf.
 * `releaseWorkflowLockRef` in `Workflow.tsx`, `releaseLockMutationRef` in
 * `useTimelineLock`). A callback re-created each render would either churn the
 * store or leave a stale closure here.
 */

import { create } from "zustand";

export type HeldLockKind = "workflow" | "timeline";

export interface HeldLock {
  kind: HeldLockKind;
  /** Workflow id or timeline id — the resource the lock is held against. */
  resourceId: string;
  /** Capability token, when the hook knows it (see power#1142). */
  lockId: string | null;
  /** The owning hook's own release path. Stable identity — see the note above. */
  release: () => Promise<void>;
}

interface HeldLocksStore {
  locks: HeldLock[];
  /** Record (or refresh) a lock this tab owns. Idempotent per kind+resourceId. */
  register: (lock: HeldLock) => void;
  /** Forget a lock this tab no longer owns. Safe to call when absent. */
  unregister: (kind: HeldLockKind, resourceId: string) => void;
  /** Non-reactive snapshot for imperative callers (the reload handler). */
  snapshot: () => HeldLock[];
}

const sameLock = (a: HeldLock, kind: HeldLockKind, resourceId: string) =>
  a.kind === kind && a.resourceId === resourceId;

export const useHeldLocksStore = create<HeldLocksStore>((set, get) => ({
  locks: [],

  register: (lock) =>
    set((state) => {
      const existing = state.locks.find((l) =>
        sameLock(l, lock.kind, lock.resourceId),
      );
      // No-op when nothing changed: registration is called from renewal paths
      // that run on an interval, and a fresh array each tick would re-render
      // every subscriber for no reason.
      if (
        existing &&
        existing.lockId === lock.lockId &&
        existing.release === lock.release
      ) {
        return state;
      }
      return {
        locks: [
          ...state.locks.filter(
            (l) => !sameLock(l, lock.kind, lock.resourceId),
          ),
          lock,
        ],
      };
    }),

  unregister: (kind, resourceId) =>
    set((state) => {
      const next = state.locks.filter((l) => !sameLock(l, kind, resourceId));
      return next.length === state.locks.length ? state : { locks: next };
    }),

  snapshot: () => get().locks,
}));
